import classNames from 'classnames';
import React, { FC, useCallback, useMemo } from 'react';
import { useLocation } from '../../../../../context/LocationContext';
import { usePaymentContext } from '../../../../../context/PaymentContextProvider';
import { comparePaymentMethod, toPaymentTuple } from '../../../../../utils/payment';
import PaymentButton from './PaymentButton';
import { useCartIsBusy } from '../../../../../hooks/useCartIsBusy';
import { useCheckout } from '../../../../../context/CheckoutContext';
import { useCartStore } from '../../../../../context/cart/CartStore';
import { shallow } from 'zustand/shallow';
import { useDirectSalesPaymentMethods } from '../../../../../context/PaymentMethodStore';
import { PaymentMethodDto } from '../../../../../../generated-api';
import AddPaymentMethodButton from './AddPaymentMethodButton';
import { useTerminalsPolling } from '../../../../../hooks/useTerminalsPolling';

const PaymentButtons: FC = () => {
  const { location } = useLocation();
  const { cartIsEmpty, giftCardPhoneNumber, vatType } = useCartStore(
    (state) => ({
      cartIsEmpty: state.cart.orderLines.length < 1,
      giftCardPhoneNumber: state.giftCardPhoneNumber,
      vatType: state.cart.vatType,
    }),
    shallow
  );
  const { activePayments, initiatePayment, setFocusedPayment } = usePaymentContext();
  const directSalesPaymentMethods = useDirectSalesPaymentMethods();

  // Poll terminal statuses for integrated payment methods
  const terminalIds = useMemo(
    () => directSalesPaymentMethods.filter((pm) => pm.provider !== 'UNINTEGRATED').map((pm) => pm.id),
    [directSalesPaymentMethods]
  );
  useTerminalsPolling(terminalIds);

  const { setComment, setToDefaultDeliveryMethod, comment, tableNumber, deliveryMethod, setSidebarContent } =
    useCheckout();
  const cartIsBusy = useCartIsBusy();

  const onSuccess = useCallback(() => {
    setToDefaultDeliveryMethod();
    setComment(undefined);
  }, [setComment, setToDefaultDeliveryMethod]);

  const handleInitiatePayment = useCallback(
    (paymentMethod: PaymentMethodDto, dynamicFields?: Record<string, string>) => {
      if (location.tableNumberRequiredForDirectSale && !tableNumber) {
        setSidebarContent('SELECT_TABLE');
        return;
      }

      if (cartIsEmpty || cartIsBusy) {
        return;
      }

      initiatePayment({
        paymentMethod,
        comment,
        onSuccess,
        tableNumber,
        deliveryMethod,
        dynamicFields,
        giftCardPhoneNumber,
        vatType,
      });
    },
    [
      cartIsBusy,
      cartIsEmpty,
      comment,
      deliveryMethod,
      giftCardPhoneNumber,
      initiatePayment,
      onSuccess,
      tableNumber,
      vatType,
      location.tableNumberRequiredForDirectSale,
      setSidebarContent,
    ]
  );

  const isMoreThanTwoPaymentMethods = directSalesPaymentMethods.length > 2;

  const renderedButtons = useMemo(() => {
    return directSalesPaymentMethods
      .sort(comparePaymentMethod)
      .map(toPaymentTuple(activePayments || []))
      .map(([paymentMethod, activePayment]) => (
        <PaymentButton
          key={paymentMethod.paymentMethod + paymentMethod.id}
          paymentMethod={paymentMethod}
          activePayment={activePayment}
          initiatePayment={handleInitiatePayment}
          setFocusedPayment={setFocusedPayment}
          shouldGrow={!isMoreThanTwoPaymentMethods}
          canTryPaymentAnyway={!cartIsEmpty}
        />
      ));
  }, [
    directSalesPaymentMethods,
    activePayments,
    handleInitiatePayment,
    setFocusedPayment,
    isMoreThanTwoPaymentMethods,
    cartIsEmpty,
  ]);

  const className = classNames({
    'overflow-x-auto': isMoreThanTwoPaymentMethods,
    'flex pb-4': true,
  });

  if (renderedButtons.length === 0) {
    return (
      <div className="flex pb-4 px-4">
        <AddPaymentMethodButton />
      </div>
    );
  }

  return <div className={className}>{renderedButtons}</div>;
};

export default React.memo(PaymentButtons);
